import { Container, Slide, useScrollTrigger } from "@mui/material";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { useAppSelector } from "hooks";
import useBreakpoints from "hooks/useBreakpoints";
import { useNavigationMenu } from "hooks/useNavigationMenu";
import { useEffect, useRef, useState } from "react";
import { selectPoisBountyV3State } from "redux/slices";
import MainMenu from "./lib/MainMenu";
import { SectionMenu } from "./lib/SectionMenu";
import {
	MainMenuContainer,
	mainMenuContainerInnerSxProps,
	Nav,
	SectionMenuContainer,
	sectionMenuContainerSxProps,
	sectionMenuInnerSxProps
} from "./styled";
import { ISectionMenuItem } from "./types";

const PageTopNav = ({
	sectionMenuData: sectionItems,
	isAppBarFixed = true,
	isLandingPage = false,
	navItemColor
}: {
	sectionMenuData: ISectionMenuItem[];
	className?: string;
	isAppBarFixed?: boolean;
	isLandingPage?: boolean;
	navItemColor?: string;
}): JSX.Element => {
	const [isLoaded, setIsLoaded] = useState<boolean>(false);
	const trigger = useScrollTrigger({ threshold: 35 });
	const sliderRef = useRef<HTMLElement | null>(null);
	const { isSmDown, isLgDown } = useBreakpoints();
	const isHideOnScrollEnabled = false;
	const top = (() => {
		if (isAppBarFixed) {
			if (isHideOnScrollEnabled && trigger) {
				return "0"; // when AppBar hidden (if HideOnScroll enabled)
			}
			return isSmDown ? "82px" : isLgDown ? "63px" : "65px";
		}
		// absolute app bar
		return "3em";
	})();

	// this disables the transition effect when switching between "pages"
	useEffect(() => {
		setTimeout(() => {
			setIsLoaded(true);
		}, 0);
	}, []);

	const { userTracker, nowTime } = useWebStoreContext();
	const { eventActive: isPoisActive } = useAppSelector(selectPoisBountyV3State);

	const navigation = useNavigationMenu(
		userTracker?.clubId,
		isPoisActive,
		userTracker,
		nowTime
	).filter(
		(navItem) =>
			!navItem?.isExternal &&
			navItem?.isEnabled &&
			navItem?.isDisplayInPageTopNav
	);

	return (
		<>
			<Slide
				ref={sliderRef}
				appear={false}
				direction="down"
				in={true}
				style={{
					zIndex: 100
				}}
				timeout={{ enter: 300, exit: 300 }}
			>
				<MainMenuContainer
					nav-item-color={navItemColor}
					className="main-menu-container"
					sx={{
						background: isLandingPage ? "none" : "",
						a: {
							color: isLandingPage ? "white" : "",
							textShadow: isLandingPage ? "1px 1px 1px #444" : ""
						}
					}}
				>
					<Container
						className="main-menu-container-inner"
						sx={{
							...mainMenuContainerInnerSxProps,
							padding: { xs: "0px", lg: "0px" }
						}}
					>
						<Nav>
							<MainMenu pages={navigation} />
						</Nav>
					</Container>
				</MainMenuContainer>
			</Slide>
			<SectionMenuContainer
				className="section-menu-container"
				sx={{
					...sectionMenuContainerSxProps,
					width: "100%",
					top: { xs: top || "82px", sm: top || "65px", lg: "60px" },
					transition: isLoaded ? "top 0.18s linear" : "none"
				}}
			>
				<Container
					className="section-menu-inner"
					sx={{ ...sectionMenuInnerSxProps }}
				>
					<SectionMenu items={sectionItems} />
				</Container>
			</SectionMenuContainer>
		</>
	);
};

export default PageTopNav;
export * from "./types";
